// src/components/Users.js
import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Papa from "papaparse";
import "./Users.css";

const alertTypes = ["Blinks", "Outages", "Voltage", "Neutral"];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // 'add', 'edit', 'password'
  const [isLoading, setIsLoading] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [error, setError] = useState("");

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    fetch("/api/users")
      .then((res) => res.json())
      .then((data) => {
        if (!isAdmin) {
          const filtered = data.filter(
            (u) => u.username === currentUser.username
          );
          setUsers(filtered);
        } else {
          setUsers(data);
        }
      })
      .catch((err) => setError("Failed to fetch users"));
  }, [isAdmin, currentUser]);

  const handleAddUser = () => {
    setEditingUser({
      username: "",
      password: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "user",
      alertsEnabled: false,
      alertPrefs: {},
    });
    setModalType("add");
    setShowModal(true);
  };

  const handleEditUser = () => {
    const user = users.find((u) => u.username === selectedUser);
    if (!user) return;
    setEditingUser({ ...user });
    setModalType("edit");
    setShowModal(true);
  };

  const handleChangePassword = () => {
    const user = users.find((u) => u.username === selectedUser);
    if (!user) return;
    setEditingUser({ ...user });
    setPasswordInput("");
    setModalType("password");
    setShowModal(true);
  };

  const handleModalSubmit = () => {
    setIsLoading(true);
    const isNew = modalType === "add";
    const endpoint = isNew ? "/api/users/add" : "/api/users/edit";
    const method = isNew ? "POST" : "PUT";

    fetch(endpoint, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        isNew ? editingUser : { originalUsername: editingUser.username, ...editingUser }
      ),
    })
      .then((res) => res.json())
      .then(() => {
        setShowModal(false);
        setSelectedUser(null);
        setEditingUser(null);
        window.location.reload();
      })
      .catch(() => alert("Error saving user."))
      .finally(() => setIsLoading(false));
  };

  const handlePasswordSubmit = () => {
    if (!editingUser || !passwordInput) return;
    fetch("/api/users/reset-password-admin", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: editingUser.username, newPassword: passwordInput }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          alert("Password changed successfully.");
          setShowModal(false);
        } else {
          alert("Password change failed.");
        }
      })
      .catch(() => alert("Error changing password"));
  };

  const updateAlertPrefs = (type, method, checked) => {
    const prefs = { ...(editingUser.alertPrefs || {}) };
    prefs[type] = prefs[type] || { email: false, sms: false };
    prefs[type][method] = checked;
    setEditingUser({ ...editingUser, alertPrefs: prefs });
  };

  const handleDeleteUser = () => {
    if (!selectedUser) return;
    if (!window.confirm("Delete selected user?")) return;
    fetch("/api/users/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ usernames: [selectedUser] }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setUsers(users.filter((u) => u.username !== selectedUser));
          setSelectedUser(null);
        } else {
          alert("Error deleting user. Please try again.");
        }
      })
      .catch(() => alert("Error deleting user. Please try again."));
  };

  const handleExportCSV = () => {
    const csv = Papa.unparse(users);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "users.csv");
    document.body.appendChild(link);
    link.click();
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      complete: (result) => {
        fetch("/api/users/import", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ users: result.data }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.success) setUsers(data.users);
          })
          .catch(() => alert("Error importing users."));
      },
    });
  };

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />
      <div style={{ padding: "2rem", flex: 1 }}>
        <h2>User Management</h2>

        {isAdmin && (
          <>
            <button onClick={handleAddUser}>Add User</button>
            <button onClick={handleEditUser} disabled={!selectedUser} style={{ marginLeft: "1rem" }}>
              Edit
            </button>
            <button
              onClick={handleDeleteUser}
              disabled={!selectedUser}
              style={{ marginLeft: "1rem", background: "red", color: "white" }}
            >
              Delete Selected
            </button>
            <button onClick={handleExportCSV} style={{ marginLeft: "1rem" }}>Export CSV</button>
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ marginLeft: "1rem" }} />
            <button onClick={handleChangePassword} disabled={!selectedUser} style={{ marginLeft: "1rem" }}>
              Change Password
            </button>
          </>
        )}

        <table style={{ width: "100%", marginTop: "1rem" }}>
          <thead>
            <tr style={{ background: "#ddd" }}>
              {isAdmin && <th></th>}
              <th>Username</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Alerts Enabled</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.username}>
                {isAdmin && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedUser === u.username}
                      onChange={() =>
                        setSelectedUser(selectedUser === u.username ? null : u.username)
                      }
                    />
                  </td>
                )}
                <td>{u.username}</td>
                <td>{u.firstName}</td>
                <td>{u.lastName}</td>
                <td>{u.email}</td>
                <td>{u.phone}</td>
                <td>{u.role}</td>
                <td>{u.alertsEnabled ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "2rem",
                borderRadius: "12px",
                width: "500px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <h3 style={{ textAlign: "center" }}>
                {modalType === "add"
                  ? "Add User"
                  : modalType === "edit"
                  ? "Edit User"
                  : "Change Password"}
              </h3>

              {modalType === "password" ? (
                <>
                  <input value={editingUser.username} readOnly />
                  <input
                    type="password"
                    placeholder="New Password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                  />
                  <div style={{ textAlign: "right" }}>
                    <button onClick={() => setShowModal(false)} style={{ marginRight: "1rem" }}>
                      Cancel
                    </button>
                    <button
                      onClick={handlePasswordSubmit}
                      disabled={!passwordInput}
                      style={{ backgroundColor: "green", color: "white" }}
                    >
                      Save Password
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <input
                    placeholder="Username"
                    value={editingUser.username}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, username: e.target.value })
                    }
                    disabled={modalType !== "add"}
                  />
                  <input
                    placeholder="Password"
                    type="password"
                    value={editingUser.password}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, password: e.target.value })
                    }
                  />
                  <input
                    placeholder="First Name"
                    value={editingUser.firstName}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, firstName: e.target.value })
                    }
                  />
                  <input
                    placeholder="Last Name"
                    value={editingUser.lastName}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, lastName: e.target.value })
                    }
                  />
                  <input
                    placeholder="Email"
                    value={editingUser.email}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, email: e.target.value })
                    }
                  />
                  <input
                    placeholder="Phone"
                    value={editingUser.phone}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, phone: e.target.value })
                    }
                  />
                  <select
                    value={editingUser.role}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, role: e.target.value })
                    }
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>

                  <label>
                    <input
                      type="checkbox"
                      checked={editingUser.alertsEnabled}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          alertsEnabled: e.target.checked,
                        })
                      }
                    />{" "}
                    Enable Alerts
                  </label>

                  {editingUser.alertsEnabled &&
                    alertTypes.map((type) => (
                      <div key={type} style={{ marginLeft: "1rem" }}>
                        <strong>{type}:</strong>
                        <label style={{ marginLeft: "1rem" }}>
                          <input
                            type="checkbox"
                            checked={editingUser.alertPrefs?.[type]?.email || false}
                            onChange={(e) =>
                              updateAlertPrefs(type, "email", e.target.checked)
                            }
                          />{" "}
                          Email
                        </label>
                        <label style={{ marginLeft: "1rem" }}>
                          <input
                            type="checkbox"
                            checked={editingUser.alertPrefs?.[type]?.sms || false}
                            onChange={(e) =>
                              updateAlertPrefs(type, "sms", e.target.checked)
                            }
                          />{" "}
                          SMS
                        </label>
                      </div>
                    ))}

                  <div style={{ textAlign: "right" }}>
                    <button onClick={() => setShowModal(false)} style={{ marginRight: "1rem" }}>
                      Cancel
                    </button>
                    <button
                      onClick={handleModalSubmit}
                      disabled={isLoading}
                      style={{ backgroundColor: "#007bff", color: "white" }}
                    >
                      {isLoading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
